import { useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ai, isApiError } from '@repletoryreach/sdk-api';
import { Stack, Group, Text, Button, Badge } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { ReplyHtml } from '../../app/assistant/ReplyHtml';
import { useCredits } from '../../lib/credits';
import { formatCount } from '../../lib/format';
import { useT, type Translate } from '../../i18n/I18nProvider';

export const MODELS_KEY = ['ai', 'models'];

/** The models on offer, each with this business's price per unit. */
export function useAiModels() {
  return useQuery({ queryKey: MODELS_KEY, queryFn: () => ai.listModels(), staleTime: 60_000 });
}

/** "0,34 credits read and 1,68 written per 10 000 tokens", or the per-unit price of another kind of model. */
export function priceLine(t: Translate, credits: (minor: number) => string, m: ai.AiModel): string {
  if (!m.priced) return t('ai.model.unpriced');
  if (m.kind === 'text' && m.prices.length >= 2)
    return t('ai.model.priceText', { read: credits(m.prices[0].rate_minor), written: credits(m.prices[1].rate_minor) });
  const p = m.prices[0];
  return p ? t('ai.model.priceUnit', { price: credits(p.rate_minor), unit: t(`billing.unit.${p.unit}.one`, p.unit ?? '') }) : '';
}

/** The models of one kind that can be used now: switched on and priced. */
export function useModelsOf(kind: ai.AiModelKind): ai.AiModel[] {
  const q = useAiModels();
  return (q.data?.data ?? []).filter((m) => m.kind === kind && m.priced);
}

/**
 * The model a tool will use: the models of its kind on offer (for text, only the ones that read pictures when a
 * picture is sent), with the price under the field. Empty picks the first on offer, as the API does.
 */
export function ModelSelect({ value, onChange, needsImages = false, kind = 'text' }: {
  value: string;
  onChange: (code: string) => void;
  needsImages?: boolean;
  kind?: ai.AiModelKind;
}) {
  const t = useT();
  const credits = useCredits();
  const models = useModelsOf(kind).filter((m) => !needsImages || m.accepts_images);
  const chosen = models.find((m) => m.code === value) ?? models[0];
  return (
    <Stack gap={4}>
      <Select
        label={t('ai.model.label')}
        value={chosen?.code ?? ''}
        onChange={(e) => onChange(e.currentTarget.value)}
        data={models.map((m) => ({ value: m.code, label: `${m.name} (${m.provider})` }))}
        description={chosen ? `${chosen.description ?? ''} ${priceLine(t, credits, chosen)}`.trim() : t('ai.model.none')}
        disabled={models.length === 0}
      />
      {chosen?.outside_south_africa && <div><Badge size="xs" color="warning" variant="light">{t('ai.model.outsideSa')}</Badge></div>}
    </Stack>
  );
}

/** What a tool produced: formatted, with what it cost and a Copy button. */
export function AiOutput({ result }: { result: ai.AiTextResult }) {
  const t = useT();
  const credits = useCredits();
  const box = useRef<HTMLDivElement | null>(null);
  const json = result.json !== undefined && result.json !== null ? JSON.stringify(result.json, null, 2) : null;

  async function copy() {
    const text = json ?? box.current?.innerText ?? result.text;
    try {
      await navigator.clipboard.writeText(text);
      notifySuccess(t('ai.result.copied'), t('ai.title'));
    } catch { /* the browser refused: the text stays on the page to select */ }
  }

  return (
    <Stack gap="xs" className="rr-ai__result" data-testid="ai-result">
      {result.stub && <Alert color="info">{t('ai.result.stub')}</Alert>}
      <div ref={box} className="rr-ai__output">
        {json ? <pre className="rr-ai__json">{json}</pre> : <ReplyHtml html={result.text} />}
      </div>
      <Group justify="space-between" wrap="wrap" gap="xs">
        <Text role="caption" c="var(--rr-color-text-muted)" data-testid="ai-cost">
          {t('ai.result.cost', {
            model: result.model_name,
            cost: credits(result.charged_minor),
            read: formatCount(result.usage.input_tokens),
            written: formatCount(result.usage.output_tokens)
          })}
        </Text>
        <Button size="xs" variant="subtle" onClick={copy}>{t('ai.result.copy')}</Button>
      </Group>
    </Stack>
  );
}

/** A refusal in words the business can act on: top up, choose another model, or try again. */
export function AiError({ error }: { error: unknown }) {
  const t = useT();
  const credits = useCredits();
  if (!error) return null;
  if (isApiError(error) && error.code === 'INSUFFICIENT_CREDITS') {
    const body = error.body as { error?: { required_minor?: number; available_minor?: number } } | null;
    return (
      <Alert color="warning" role="alert" title={t('ai.error.credits.title')}>
        <Stack gap={6}>
          <Text role="bodySmall">{t('ai.error.credits.body', {
            needed: credits(body?.error?.required_minor ?? 0), available: credits(Math.max(0, body?.error?.available_minor ?? 0))
          })}</Text>
          <div><Link to="/billing/topup">{t('ai.error.credits.topUp')}</Link></div>
        </Stack>
      </Alert>
    );
  }
  const code = isApiError(error) ? error.code : null;
  const known = code && ['AI_MODEL_UNAVAILABLE', 'AI_MODEL_NOT_PRICED', 'AI_PROVIDER_ERROR', 'BILLING_UNAVAILABLE'].includes(code);
  return (
    <Alert color="error" role="alert">
      {known ? t(`ai.error.${code}`) : error instanceof Error ? error.message : t('common.unknownError')}
    </Alert>
  );
}

/** The format every tool on this page asks for: simple HTML, shown with the console's safe renderer. */
export const PAGE_FORMAT = 'html' as const;

/** A file read in the browser as a data: URL, for the tools that send a picture or a recording. */
export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** What a speech, transcription or picture call cost, and that a copy is in the media library when it was saved. */
export function UnitCost({ minor, saved, children }: { minor: number; saved?: boolean; children?: ReactNode }) {
  const t = useT();
  const credits = useCredits();
  return (
    <Group justify="space-between" wrap="wrap" gap="xs">
      <Text role="caption" c="var(--rr-color-text-muted)" data-testid="ai-cost">
        {t('ai.unit.cost', { cost: credits(minor) })}{saved ? ` · ${t('ai.unit.saved')}` : ''}
      </Text>
      {children}
    </Group>
  );
}
